import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TuiHint, TuiIcon, TuiLabel, TuiTextfield } from '@taiga-ui/core';
import { TuiTextarea } from '@taiga-ui/kit';
import { TranslatePipe } from '@ngx-translate/core';
import {
  MASS_FILTER_VALUE_MAX_LENGTH,
  MASS_FILTER_VALUES_LIMIT,
} from '@outkeep/shared';
import { formatMassFilterInput, parseMassFilterInput } from '@core/helpers';
import { DismissMobileHintDirective } from '@common/directives/dismiss-mobile-hint.directive';
import type { MassFilterInputHint } from '../../types';

/** Счётчик экземпляров: у каждого поля свой id, иначе подсказки перепутаются. */
let instanceCounter = 0;

/**
 * Поле фильтра, принимающее набор значений: пользователь вставляет столбец
 * из таблицы (Ctrl+V) или вводит значения вручную. Текст разбирается на
 * значения той же парой «разбор + нормализация», что и на сервере, поэтому
 * количество в счётчике совпадает с тем, по чему реально идёт поиск.
 */
@Component({
  selector: 'x-mass-filter-input',
  imports: [
    NgClass,
    FormsModule,
    TuiTextfield,
    TuiTextarea,
    TuiLabel,
    TuiIcon,
    TuiHint,
    DismissMobileHintDirective,
    TranslatePipe,
  ],
  templateUrl: './mass-filter-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MassFilterInputComponent {
  readonly $values = model<string[]>([], { alias: 'values' });
  readonly $label = input<string>('', { alias: 'label' });
  readonly $placeholder = input<string>('', { alias: 'placeholder' });
  /** Сравнивать значения без учёта регистра — для VIN, как делает сервер. */
  readonly $caseInsensitive = input<boolean>(false, {
    alias: 'caseInsensitive',
  });
  readonly $showLabel = input<boolean>(true, { alias: 'showLabel' });
  /** Класс для самого tui-textfield: стили фильтров шапки таблицы. */
  readonly $fieldClass = input<string>('', { alias: 'fieldClass' });

  /** Набор недопустим: искать по нему нельзя, пока его не исправят. */
  readonly invalidChange = output<boolean>();
  /**
   * Применить фильтр. Enter здесь — перенос строки, а не поиск, поэтому
   * набор применяется по потере фокуса и по Ctrl+Enter.
   */
  readonly apply = output<void>();

  readonly limit = MASS_FILTER_VALUES_LIMIT;
  readonly maxValueLength = MASS_FILTER_VALUE_MAX_LENGTH;
  /**
   * Идентификатор поля ввода. Иконка подсказки в таб-порядок не попадает,
   * поэтому подсказка привязана к самому полю через tuiHintDescribe: при
   * фокусе с клавиатуры её текст показывается без наведения курсора.
   */
  readonly fieldId = `x-mass-filter-input-${(instanceCounter += 1)}`;
  readonly $text = signal('');
  readonly $count = signal(0);
  readonly $overLimit = computed(() => this.$count() > this.limit);
  /**
   * Значения длиннее допустимого. Как правило это признак того, что вставили
   * не тот столбец таблицы: номера договоров и VIN заметно короче.
   */
  readonly $tooLongCount = computed(
    () =>
      this.$values().filter(value => value.length > this.maxValueLength).length,
  );
  /** Набор нельзя применять: он либо сверх лимита, либо с плохим значением. */
  readonly $invalid = computed(() => this.isInvalid(this.$values()));
  /** Счётчик нужен только для набора: одно значение и так видно в поле. */
  readonly $showCount = computed(() => this.$count() > 1);
  /**
   * По набору поиск идёт по точному совпадению, а по одному значению — по
   * вхождению подстроки. Разницу нужно показать, иначе результат по набору
   * выглядит неполным. При недопустимом наборе на месте подсказки — причина.
   */
  readonly $showExactHint = computed(
    () => !this.$invalid() && this.$count() > 1,
  );
  /**
   * Текст у иконки рядом с полем: сначала причина недопустимости, иначе —
   * подсказка о способе поиска. Поля массовых фильтров стоят в строке
   * фильтров таблицы, поэтому под полем остаётся только счётчик значений, а
   * остальное уходит в подсказку, чтобы высота строки не скакала.
   */
  readonly $hint = computed<MassFilterInputHint | null>(() => {
    if (this.$overLimit()) {
      const overLimit: MassFilterInputHint = {
        key: 'mass_filter_input.over_limit',
        params: { count: this.$count(), limit: this.limit },
        negative: true,
      };

      return overLimit;
    }

    if (this.$tooLongCount() > 0) {
      const tooLong: MassFilterInputHint = {
        key: 'mass_filter_input.value_too_long',
        params: { count: this.$tooLongCount(), length: this.maxValueLength },
        negative: true,
      };

      return tooLong;
    }

    const exactMatch: MassFilterInputHint = {
      key: 'mass_filter_input.exact_match_hint',
      params: {},
      negative: false,
    };

    return this.$showExactHint() ? exactMatch : null;
  });

  // значения приходят извне при восстановлении фильтра из сохранённого
  // состояния — текст в поле нужно привести к ним, не затирая ввод
  private lastEmitted: string[] = [];

  constructor() {
    effect(() => {
      const values = this.$values();

      if (this.isLastEmitted(values)) {
        return;
      }

      this.lastEmitted = values;
      this.$text.set(formatMassFilterInput(values));
      this.$count.set(values.length);
    });
  }

  onTextChange(text: string): void {
    this.$text.set(text);

    const values = parseMassFilterInput(text, {
      caseInsensitive: this.$caseInsensitive(),
    });

    this.$count.set(values.length);

    // о недопустимости родитель узнаёт раньше, чем о самих значениях: запись
    // в values доходит до него синхронно, и по старому признаку он успел бы
    // применить фильтр недопустимым набором
    this.invalidChange.emit(this.isInvalid(values));

    // значения записываются всегда, даже когда набор недопустим: иначе
    // состояние поля расходится со счётчиком, а сокращение набора до лимита
    // не возвращает фильтр в рабочее состояние
    this.lastEmitted = values;
    this.$values.set(values);

    // очистка поля (в том числе крестиком Taiga) снимает фильтр сразу,
    // не дожидаясь потери фокуса
    if (!values.length) {
      this.apply.emit();
    }
  }

  onApply(): void {
    if (this.$invalid()) {
      return;
    }

    this.apply.emit();
  }

  private isInvalid(values: string[]): boolean {
    return (
      values.length > this.limit ||
      values.some(value => value.length > this.maxValueLength)
    );
  }

  private isLastEmitted(values: string[]): boolean {
    return (
      values.length === this.lastEmitted.length &&
      values.every((value, index) => value === this.lastEmitted[index])
    );
  }
}
